import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";
import { useAppDispatch } from "hooks/redux";
import { roundNumber } from "helpers/Helpers";

import { StatusCode } from "api/protocols";

import Page from "components/page/Page";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Checkbox from "components/form/checkbox/Checkbox";
import Tabs from "components/tabs/Tabs";

import "pages/secure/facility/product/Variant.scss";

import { useTranslation } from "react-i18next";
import { GetProduct, PostProductOption } from "api/rpc/2024-04/facilityAdmin/product/product";
import { PostVariant, PutVariant } from "api/rpc/2024-04/facilityAdmin/product/variant";
import { showError, showSuccess } from "redux/actions/ui";
import { IProduct, IVariant } from "redux/reducers/models/product";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import SelectNew from "components/select/SelectNew";
import { GetTaxLine } from "api/rpc/2024-04/facilityAdmin/client/taxLines";
import { UpdateInventoryLevels } from "api/rpc/2024-04/facilityAdmin/product/inventory";
import { TTaxLines } from "redux/reducers/models/facility";
import { ERangeBucketSizeModal, isERangeProduct } from "pages/secure/facility/productNew/ERangeBucketSizeModal";
import { nextSelectedTaxLineIds, taxLineSelectOptions, taxLineSelectValue } from "./taxLineSelect";

function priceAfterTaxAmount(price: number, taxLineIds: number[], lines: TTaxLines[]): number {
  const original = Number(price) || 0;
  let total = original;
  for (const id of taxLineIds) {
    total += original * Number(lines.find(line => line.id === id)?.tax ?? 0);
  }
  return Number(roundNumber(total, 2));
}

function priceBeforeTax(amount: number, taxLineIds: number[], lines: TTaxLines[]): number {
  let rate = 0;
  for (const id of taxLineIds) {
    const line = lines.find(tax => tax.id === id);
    if (line) {
      rate += Number(line.tax);
    }
  }
  return amount / (1 + rate);
}

function taxLineNames(taxLineIds: number[], lines: TTaxLines[]): string {
  return taxLineIds
    .map(id => lines.find(line => line.id === id)?.name ?? "")
    .filter(Boolean)
    .join(", ");
}

function variantIdFromResponse(data: unknown): number | null {
  if (data && typeof data === "object" && "id" in data) {
    const id = Number((data as { id: unknown }).id);
    return Number.isFinite(id) && id > 0 ? id : null;
  }
  return null;
}

function existingVariantOptionValue(variant: IVariant): string {
  return (variant.title ?? "").trim() || (variant.sku ?? "").trim() || `Variant ${variant.id}`;
}

interface IVariantState {
  product_id: number;
  option1: string;
  option2: string;
  option3: string;
  price: number;
  cost: number;
  taxable: boolean;
  barcode: string;
  sku: string;
  tracked: boolean;
  cannot_be_sold: boolean;
  product: IProduct | null;
}

interface IBootstrapState {
  productOption: string;
  existingOptionValue: string;
  existingOptionPrice: number;
  existingOptionCost: number;
  existingOptionTaxable: boolean;
  existingOptionCannotBeSold: boolean;
  newOptionValue: string;
}

export default function VariantNew() {
  const history = useHistory();
  const { t } = useTranslation();
  const { productId }: { productId?: string } = useParams();
  const dispatch = useAppDispatch();

  const [variantState, setVariantState] = useState<IVariantState>({
    product_id: null,
    option1: "",
    option2: "",
    option3: "",
    price: null,
    cost: null,
    taxable: false,
    barcode: "",
    sku: "",
    tracked: false,
    cannot_be_sold: false,
    product: null,
  });

  const [bootstrapState, setBootstrapState] = useState<IBootstrapState>({
    productOption: "",
    existingOptionValue: "",
    existingOptionPrice: null,
    existingOptionCost: null,
    existingOptionTaxable: false,
    existingOptionCannotBeSold: false,
    newOptionValue: "",
  });

  const [selected, setSelected] = useState(0);
  const [markup, setMarkup] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [eRangeBucketModalOpen, setERangeBucketModalOpen] = useState(false);
  const [taxLines, setTaxLines] = useState<TTaxLines[]>([]);
  const [selectedTaxLine, setSelectedTaxLine] = useState<number[]>([]);
  const [priceAfterTax, setPriceAfterTax] = useState(0);
  const [hidePriceAfterTax, setHidePriceAfterTax] = useState(false);
  const [existingSelectedTaxLine, setExistingSelectedTaxLine] = useState<number[]>([]);
  const [existingPriceAfterTax, setExistingPriceAfterTax] = useState(0);
  const [hideExistingPriceAfterTax, setHideExistingPriceAfterTax] = useState(false);

  const isBootstrapMode =
    !!variantState.product &&
    (variantState.product.variants?.length ?? 0) === 1 &&
    ((variantState.product.options?.length ?? 0) === 0 || !variantState.product.variants?.[0]?.option1);

  const existingOptionName = variantState.product?.options?.[0]?.name ?? "";
  const optionNameLocked = isBootstrapMode && (variantState.product?.options?.length ?? 0) > 0;
  const needsLegacyOptionBackfill =
    !!variantState.product && !isBootstrapMode && (variantState.product.options?.length ?? 0) === 0;
  const regularCombo =
    variantState.product?.type === "Combo" && variantState.product?.dynamic_combo !== true;

  const tabs = [
    { id: "overview", content: t("Overview") },
    { id: "pricing", content: t("Pricing & Taxes") },
    { id: "Logistics", content: t("Logistics") },
  ];

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = event.target;
    setVariantState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleCheckboxChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = event.target;
    setVariantState(prevState => ({ ...prevState, [id]: checked }));
  }

  function handleBootstrapChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, value, type, checked } = event.target;
    const nextValue = type === "checkbox" ? checked : type === "number" ? (value === "" ? null : Number(value)) : value;
    setBootstrapState(prev => ({
      ...prev,
      [id]: nextValue,
    }));
    if (id === "existingOptionPrice") {
      setExistingPriceAfterTax(
        priceAfterTaxAmount(Number(nextValue ?? 0), existingSelectedTaxLine, taxLines),
      );
    }
  }

  const handleTabChange = (selectedTabIndex: number) => {
    setSelected(selectedTabIndex);
  };

  useEffect(() => {
    void loadProduct();
  }, []);

  async function loadProduct() {
    const [productRes, taxLineRes] = await Promise.all([
      GetProduct({ id: productId, extended: true }, true),
      GetTaxLine(null, false),
    ]);
    if (productRes.status !== StatusCode.OK || !productRes.data?.[0]) {
      return;
    }

    const product = productRes.data[0];
    const defaultVariant = product.variants?.[0];
    const loadedTaxLines = taxLineRes.status === StatusCode.OK ? taxLineRes.data ?? [] : [];
    const savedTaxLineIds = defaultVariant?.inventory_item?.inventory_level?.tax_lines?.map(line => line.id) ?? [];
    const startingPrice = defaultVariant?.price ?? null;

    setTaxLines(loadedTaxLines);
    setSelectedTaxLine(savedTaxLineIds);
    setExistingSelectedTaxLine(savedTaxLineIds);
    if (startingPrice != null) {
      const startingPriceAfterTax = priceAfterTaxAmount(Number(startingPrice), savedTaxLineIds, loadedTaxLines);
      setPriceAfterTax(startingPriceAfterTax);
      setExistingPriceAfterTax(startingPriceAfterTax);
    }

    setVariantState(prev => ({
      ...prev,
      product_id: product.id,
      product,
      price: defaultVariant?.price ?? prev.price,
      cost: defaultVariant?.cost ?? prev.cost,
      taxable: savedTaxLineIds.length > 0,
    }));

    if ((product.options?.length ?? 0) === 0 && defaultVariant) {
      setBootstrapState(prev => ({
        ...prev,
        existingOptionPrice: defaultVariant.price ?? null,
        existingOptionCost: defaultVariant.cost ?? null,
        existingOptionTaxable: savedTaxLineIds.length > 0,
        existingOptionCannotBeSold: !!defaultVariant.cannot_be_sold,
      }));
      if (defaultVariant.cost && defaultVariant.price != null) {
        const cost = Number(defaultVariant.cost);
        if (cost > 0) {
          setMarkup(((Number(defaultVariant.price) - cost) / cost) * 100);
        }
      }
    } else if ((product.variants?.length ?? 0) === 1 && !defaultVariant?.option1) {
      // Resume incomplete bootstrap (option exists, default variant not yet optioned)
      setBootstrapState(prev => ({
        ...prev,
        productOption: product.options?.[0]?.name ?? "",
        existingOptionPrice: defaultVariant?.price ?? null,
        existingOptionCost: defaultVariant?.cost ?? null,
        existingOptionTaxable: savedTaxLineIds.length > 0,
        existingOptionCannotBeSold: !!defaultVariant?.cannot_be_sold,
      }));
    }
  }

  function validatePrice(price: number | null, cost: number | null): boolean {
    if (price == null || Number.isNaN(Number(price))) {
      dispatch(showError(t("Missing Pricing Information")));
      return false;
    }
    if (cost != null && !Number.isNaN(Number(cost)) && Number(price) < Number(cost)) {
      dispatch(showError(t("Retail Price must be greater than Cost")));
      return false;
    }
    return true;
  }

  async function createBootstrapVariant(bucketSize?: number) {
    const product = variantState.product;
    const existingVariant = product?.variants?.[0];
    if (!product?.id || !existingVariant?.id) {
      dispatch(showError(t("Product variant not found")));
      return;
    }

    const optionName = (optionNameLocked ? existingOptionName : bootstrapState.productOption).trim();
    if (!optionName) {
      dispatch(showError(t("Option name is required")));
      return;
    }
    if (!bootstrapState.existingOptionValue.trim()) {
      dispatch(showError(t("Existing variant option value is required")));
      return;
    }
    if (!bootstrapState.newOptionValue.trim()) {
      dispatch(showError(t("New variant option value is required")));
      return;
    }
    if (!validatePrice(bootstrapState.existingOptionPrice, bootstrapState.existingOptionCost)) {
      return;
    }
    if (!validatePrice(variantState.price, variantState.cost)) {
      return;
    }

    setSaving(true);
    try {
      let latestProduct = product;
      const existingOption = latestProduct.options?.find(
        option => option.name?.toLowerCase() === optionName.toLowerCase(),
      );

      if (!existingOption) {
        const res = await PostProductOption({ name: optionName, product_id: latestProduct.id }, true);
        if (res.status !== StatusCode.OK) {
          dispatch(
            showError(typeof res.data === "string" ? res.data : t("Failed to create product option")),
          );
          return;
        }
      }

      const refreshed = await GetProduct({ id: latestProduct.id, extended: true }, false);
      if (refreshed.status === StatusCode.OK && refreshed.data?.[0]) {
        latestProduct = refreshed.data[0];
        setVariantState(prev => ({ ...prev, product: latestProduct }));
      }

      const optionCount = latestProduct.options?.length ?? 0;
      const refreshedExisting = latestProduct.variants?.[0];
      if (!refreshedExisting?.id) {
        dispatch(showError(t("Product variant not found")));
        return;
      }

      const option1 = bootstrapState.existingOptionValue.trim();
      const option2 =
        optionCount > 1 ? refreshedExisting.option2 || option1 : undefined;
      const option3 =
        optionCount > 2 ? refreshedExisting.option3 || option1 : undefined;

      const putVariantParams: Record<string, unknown> = {
        id: refreshedExisting.id,
        option1,
        // API create builds title from options; put keeps the old title unless we send one
        title: [option1, option2, option3].filter(Boolean).join(" / "),
        price: bootstrapState.existingOptionPrice,
        cost: bootstrapState.existingOptionCost,
        taxable: existingSelectedTaxLine.length > 0,
        cannot_be_sold: bootstrapState.existingOptionCannotBeSold,
      };
      if (option2) {
        putVariantParams.option2 = option2;
      }
      if (option3) {
        putVariantParams.option3 = option3;
      }

      const putVariantRes = await PutVariant(putVariantParams as Parameters<typeof PutVariant>[0], true);
      if (putVariantRes.status !== StatusCode.OK) {
        dispatch(
          showError(typeof putVariantRes.data === "string" ? putVariantRes.data : t("Failed to update existing variant")),
        );
        return;
      }

      const existingTaxRes = await UpdateInventoryLevels(
        { variant_id: refreshedExisting.id, tax_line_ids: existingSelectedTaxLine },
        true,
      );
      if (existingTaxRes.status !== StatusCode.OK) {
        dispatch(showError(t("secure.facility.product.use_edit_product.031")));
        return;
      }

      const postParams: Record<string, unknown> = {
        product_id: latestProduct.id,
        option1: bootstrapState.newOptionValue.trim(),
        price: variantState.price,
        cost: variantState.cost,
        taxable: selectedTaxLine.length > 0,
        cannot_be_sold: variantState.cannot_be_sold,
        barcode: variantState.barcode || undefined,
        sku: variantState.sku || undefined,
        tracked: variantState.tracked,
        ...(isERangeProduct(latestProduct.type) && bucketSize != null
          ? { integration_data: { e_range_bucket_size: bucketSize } }
          : {}),
      };
      if (optionCount > 1) {
        postParams.option2 = refreshedExisting.option2 || bootstrapState.newOptionValue.trim();
      }
      if (optionCount > 2) {
        postParams.option3 = refreshedExisting.option3 || bootstrapState.newOptionValue.trim();
      }

      const variantRes = await PostVariant(postParams as Parameters<typeof PostVariant>[0], true);
      if (variantRes.status !== StatusCode.OK) {
        dispatch(
          showError(typeof variantRes.data === "string" ? variantRes.data : t("Failed to create variant")),
        );
        return;
      }

      const createdVariantId = variantIdFromResponse(variantRes.data);
      if (createdVariantId) {
        const taxRes = await UpdateInventoryLevels(
          { variant_id: createdVariantId, tax_line_ids: selectedTaxLine },
          true,
        );
        if (taxRes.status !== StatusCode.OK) {
          dispatch(showError(t("secure.facility.product.use_edit_product.031")));
          history.push({ pathname: "/admin/product/" + String(productId), state: { tab: 3 } });
          return;
        }
      }

      dispatch(showSuccess(t("Successfully created product option and variant.")));
      history.push({ pathname: "/admin/product/" + String(productId), state: { tab: 3 } });
    } finally {
      setSaving(false);
    }
  }

  async function createNormalVariant(bucketSize?: number) {
    const product = variantState.product;
    const options = product?.options ?? [];
    const existingVariants = product?.variants ?? [];

    if (needsLegacyOptionBackfill) {
      if (!bootstrapState.productOption.trim()) {
        dispatch(showError(t("Option name is required")));
        return;
      }
      if (!variantState.option1?.trim()) {
        dispatch(showError(t("Option value is required")));
        return;
      }
    } else {
      if (options[0] && !variantState.option1?.trim()) {
        dispatch(showError(t("{{name}} is required", { name: options[0].name })));
        return;
      }
      if (options[1] && !variantState.option2?.trim()) {
        dispatch(showError(t("{{name}} is required", { name: options[1].name })));
        return;
      }
      if (options[2] && !variantState.option3?.trim()) {
        dispatch(showError(t("{{name}} is required", { name: options[2].name })));
        return;
      }
    }
    if (!validatePrice(variantState.price, variantState.cost)) {
      return;
    }

    setSaving(true);
    try {
      if (needsLegacyOptionBackfill) {
        const optionName = bootstrapState.productOption.trim();
        const newValue = variantState.option1.trim();
        const values = Array.from(
          new Set([...existingVariants.map(variant => existingVariantOptionValue(variant)), newValue]),
        );

        const optionRes = await PostProductOption(
          { name: optionName, product_id: variantState.product_id, values },
          true,
        );
        if (optionRes.status !== StatusCode.OK) {
          dispatch(
            showError(typeof optionRes.data === "string" ? optionRes.data : t("Failed to create product option")),
          );
          return;
        }

        for (const variant of existingVariants) {
          const option1 = existingVariantOptionValue(variant);
          const putVariantRes = await PutVariant({ id: variant.id, option1 }, true);
          if (putVariantRes.status !== StatusCode.OK) {
            dispatch(
              showError(
                typeof putVariantRes.data === "string"
                  ? putVariantRes.data
                  : t("Failed to update existing variant"),
              ),
            );
            return;
          }
        }
      }

      const variantRes = await PostVariant(
        {
          product_id: variantState.product_id,
          option1: variantState.option1.trim(),
          option2: options[1] ? variantState.option2.trim() : undefined,
          option3: options[2] ? variantState.option3.trim() : undefined,
          price: Number(variantState.price),
          cost: variantState.cost != null ? Number(variantState.cost) : undefined,
          taxable: selectedTaxLine.length > 0,
          barcode: variantState.barcode || undefined,
          sku: variantState.sku || undefined,
          tracked: variantState.tracked,
          cannot_be_sold: variantState.cannot_be_sold,
          ...(isERangeProduct(product?.type) && bucketSize != null
            ? { integration_data: { e_range_bucket_size: bucketSize } }
            : {}),
        },
        true,
      );

      if (variantRes.status !== StatusCode.OK) {
        dispatch(
          showError(typeof variantRes.data === "string" ? variantRes.data : t("Failed to create variant")),
        );
        return;
      }

      const createdVariantId = variantIdFromResponse(variantRes.data);
      if (createdVariantId) {
        const taxRes = await UpdateInventoryLevels(
          { variant_id: createdVariantId, tax_line_ids: selectedTaxLine },
          true,
        );
        if (taxRes.status !== StatusCode.OK) {
          dispatch(showError(t("secure.facility.product.use_edit_product.031")));
          history.push({ pathname: "/admin/product/" + String(productId), state: { tab: 3 } });
          return;
        }
      }

      dispatch(
        showSuccess(
          needsLegacyOptionBackfill
            ? t("Successfully created product option and variant.")
            : t("Successfully created variant."),
        ),
      );
      history.push({ pathname: "/admin/product/" + String(productId), state: { tab: 3 } });
    } finally {
      setSaving(false);
    }
  }

  async function createVariant(bucketSize?: number) {
    if (saving) {
      return;
    }
    const selectedBucketSize = typeof bucketSize === "number" ? bucketSize : undefined;
    if (isERangeProduct(variantState.product?.type) && selectedBucketSize == null) {
      setERangeBucketModalOpen(true);
      return;
    }
    if (isBootstrapMode) {
      await createBootstrapVariant(selectedBucketSize);
    } else {
      await createNormalVariant(selectedBucketSize);
    }
  }

  function handleMarkupChange(value: number) {
    if (variantState.cost == null || Number(variantState.cost) === 0) {
      dispatch(showError(t("Cost is required")));
      return;
    }
    setMarkup(value);
    const nextPrice = Number(roundNumber(Number(variantState.cost) * (value / 100) + Number(variantState.cost), 2));
    setVariantState(prev => ({ ...prev, price: nextPrice }));
    setPriceAfterTax(priceAfterTaxAmount(nextPrice, selectedTaxLine, taxLines));
  }

  function handleCostChange(value: number) {
    if (value === 0) {
      setMarkup(0);
      setVariantState(prev => ({ ...prev, cost: Number(roundNumber(value, 2)) }));
      return;
    }
    const price = Number(variantState?.price ?? 0);
    setMarkup(((price - value) / value) * 100);
    setVariantState(prev => ({ ...prev, cost: Number(roundNumber(value, 2)) }));
  }

  function handlePriceChange(value: number) {
    const cost = Number(variantState?.cost ?? 0);
    if (cost > 0) {
      setMarkup(((value - cost) / cost) * 100);
    }
    const nextPrice = Number(roundNumber(value, 2));
    setVariantState(prev => ({ ...prev, price: nextPrice }));
    setPriceAfterTax(priceAfterTaxAmount(nextPrice, selectedTaxLine, taxLines));
  }

  function handlePriceAfterTaxChange(value: number) {
    const cost = Number(variantState.cost ?? 0);
    const price = priceBeforeTax(value, selectedTaxLine, taxLines);
    if (cost > 0) {
      setMarkup(((price - cost) / cost) * 100);
    }
    setVariantState(prev => ({ ...prev, price: Number(roundNumber(price, 2)) }));
    setPriceAfterTax(value);
  }

  function handleExistingPriceAfterTaxChange(value: number) {
    const price = Number(roundNumber(priceBeforeTax(value, existingSelectedTaxLine, taxLines), 2));
    setBootstrapState(prev => ({ ...prev, existingOptionPrice: price }));
    setExistingPriceAfterTax(value);
  }

  function toggleTaxLines(value: string | number) {
    const next = nextSelectedTaxLineIds(selectedTaxLine, value);
    setSelectedTaxLine(next);
    setVariantState(prev => ({ ...prev, taxable: next.length > 0 }));
    setPriceAfterTax(priceAfterTaxAmount(Number(variantState.price ?? 0), next, taxLines));
  }

  function toggleExistingTaxLines(value: string | number) {
    const next = nextSelectedTaxLineIds(existingSelectedTaxLine, value);
    setExistingSelectedTaxLine(next);
    setBootstrapState(prev => ({ ...prev, existingOptionTaxable: next.length > 0 }));
    setExistingPriceAfterTax(priceAfterTaxAmount(Number(bootstrapState.existingOptionPrice ?? 0), next, taxLines));
  }

  const primaryAction = {
    content: t("secure.facility.product.variant_new.001"),
    action: createVariant,
    disabled: saving || !variantState.product,
  };

  const secondaryActions = [
    {
      content: t("Cancel"),
      action: () => history.push("/admin/product/" + String(productId)),
    },
  ];

  const optionInputs = [
    { key: "option1" as const, option: variantState.product?.options?.[0] },
    { key: "option2" as const, option: variantState.product?.options?.[1] },
    { key: "option3" as const, option: variantState.product?.options?.[2] },
  ].filter(item => !!item.option);

  return (
    <Page
      title={t("secure.facility.product.variant_new.002")}
      narrow
      primaryAction={primaryAction}
      secondaryActions={secondaryActions}
    >
      <ERangeBucketSizeModal
        open={eRangeBucketModalOpen}
        productTitle={variantState.product?.title}
        onCancel={() => setERangeBucketModalOpen(false)}
        onConfirm={size => {
          setERangeBucketModalOpen(false);
          void createVariant(size);
        }}
      />
      <Page.Section>
        <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange} />
      </Page.Section>
      <br />
      {selected === 0 ? (
        <Card title={isBootstrapMode ? t("Set Up Variants") : t("secure.facility.product.variant_new.003")}>
          {isBootstrapMode ? (
            <>
              <Card.Section>
                <div className="mb-4 text-sm text-gray-600 space-y-2">
                  <p>
                    {t("An option is the attribute that differs between variants — for example Size, Colour, or Quantity.")}
                  </p>
                  <p>
                    {t("Each variant then gets a value for that option — for example Small and Large, or Red and Blue.")}
                  </p>
                  <p>
                    {t("Your product already has one default SKU. Name the option, give that existing SKU its value, then add a value (and price) for the new variant.")}
                  </p>
                </div>
                <Input
                  value={optionNameLocked ? existingOptionName : bootstrapState.productOption}
                  label={t("Option name")}
                  onChange={handleBootstrapChange}
                  type="text"
                  id="productOption"
                  placeholder={t("e.g. Size")}
                  helpText={t("What differs between variants? (Size, Colour, Pack size, etc.)")}
                  disabled={optionNameLocked}
                />
              </Card.Section>
              <Card.Section
                title={
                  bootstrapState.productOption.trim() || existingOptionName
                    ? t("Current product — {{name}} value", {
                        name: bootstrapState.productOption.trim() || existingOptionName,
                      })
                    : t("Current product")
                }
              >
                <FormLayout>
                  <FormLayout.Group>
                    <Input
                      value={bootstrapState.existingOptionValue}
                      label={
                        bootstrapState.productOption.trim() || existingOptionName
                          ? t("{{name}} for current product", {
                              name: bootstrapState.productOption.trim() || existingOptionName,
                            })
                          : t("Value for current product")
                      }
                      onChange={handleBootstrapChange}
                      type="text"
                      id="existingOptionValue"
                      placeholder={t("e.g. Small")}
                      helpText={t("How should the existing SKU be labeled for this option?")}
                    />
                  </FormLayout.Group>
                </FormLayout>
              </Card.Section>
              <Card.Section
                title={
                  bootstrapState.productOption.trim() || existingOptionName
                    ? t("New variant — {{name}} value", {
                        name: bootstrapState.productOption.trim() || existingOptionName,
                      })
                    : t("New variant")
                }
              >
                <FormLayout>
                  <FormLayout.Group>
                    <Input
                      value={bootstrapState.newOptionValue}
                      label={
                        bootstrapState.productOption.trim() || existingOptionName
                          ? t("{{name}} for new variant", {
                              name: bootstrapState.productOption.trim() || existingOptionName,
                            })
                          : t("Value for new variant")
                      }
                      onChange={handleBootstrapChange}
                      type="text"
                      id="newOptionValue"
                      placeholder={t("e.g. Large")}
                      helpText={t("How should the new variant be labeled for this option?")}
                    />
                  </FormLayout.Group>
                </FormLayout>
              </Card.Section>
            </>
          ) : (
            <Card.Section>
              {needsLegacyOptionBackfill ? (
                <div className="mb-4 text-sm text-gray-600 space-y-2 flex items-center gap-2">
                  <FontAwesomeIcon icon={faExclamationCircle} size="lg"/>
                  <p>
                    {t("This product's current variants are not yet assigned to an option. Name the option (for example Size or Colour), then enter a value for the new variant.")}
                  </p>
                </div>
              ) : (
                <p className="mb-4 text-sm text-gray-600">
                  {optionInputs.length > 1
                    ? t("Enter the option value for this new variant for each option below. For example, if the option is Size, enter Medium or XL.")
                    : t("Enter the option value for this new variant. For example, if the option is Size, enter Medium or XL.")}
                </p>
              )}
              {needsLegacyOptionBackfill ? (
                <div className="space-y-4">
                  <Input
                    type="text"
                    id="productOption"
                    value={bootstrapState.productOption}
                    onChange={handleBootstrapChange}
                    label={t("Option name")}
                    placeholder={t("e.g. Size")}
                    helpText={t("What differs between variants? (Size, Colour, Pack size, etc.)")}
                  />
                  <Input
                    type="text"
                    id="option1"
                    value={variantState.option1 || ""}
                    onChange={handleInputChange}
                    label={
                      bootstrapState.productOption.trim()
                        ? t("{{name}} for new variant", { name: bootstrapState.productOption.trim() })
                        : t("Value for new variant")
                    }
                    placeholder={t("e.g. Medium")}
                    helpText={t("How should the new variant be labeled for this option?")}
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  {optionInputs.map(({ key, option }) => (
                    <Input
                      key={key}
                      type="text"
                      id={key}
                      value={variantState[key] || ""}
                      onChange={handleInputChange}
                      label={option?.name}
                      placeholder={t("e.g. value for {{name}}", { name: option?.name })}
                      helpText={t("This variant's {{name}}", { name: option?.name })}
                    />
                  ))}
                </div>
              )}
            </Card.Section>
          )}
        </Card>
      ) : null}

      {selected === 1 ? (
        <>
          {isBootstrapMode ? (
            <Card
              title={
                bootstrapState.existingOptionValue.trim()
                  ? t("Pricing — current product ({{value}})", {
                      value: bootstrapState.existingOptionValue.trim(),
                    })
                  : t("Pricing — current product")
              }
            >
              <Card.Section>
                <FormLayout>
                  <FormLayout.Group>
                    <Input
                      id="existingOptionPrice"
                      value={bootstrapState.existingOptionPrice ?? ""}
                      type="number"
                      prefix="$"
                      label={t("Retail Price")}
                      onChange={handleBootstrapChange}
                      placeholder="0.00"
                    />
                    <Input
                      id="existingOptionCost"
                      value={bootstrapState.existingOptionCost ?? ""}
                      type="number"
                      prefix="$"
                      label={t("secure.facility.product.product_new.015")}
                      onChange={handleBootstrapChange}
                      placeholder="0.00"
                    />
                  </FormLayout.Group>
                </FormLayout>
              </Card.Section>
              <Card.Section
                title={t("Price after tax")}
                sectionTitleActions={[
                  {
                    content: <FontAwesomeIcon icon={hideExistingPriceAfterTax ? faChevronUp : faChevronDown} />,
                    action: () => setHideExistingPriceAfterTax(!hideExistingPriceAfterTax),
                  },
                ]}
              >
                {!hideExistingPriceAfterTax ? (
                  <FormLayout>
                    <FormLayout.Group>
                      <SelectNew
                        id="existing-tax-lines-select"
                        label={t("Tax lines")}
                        placeholder={
                          existingSelectedTaxLine.length === 0
                            ? t("No tax")
                            : taxLineNames(existingSelectedTaxLine, taxLines) || t("Select tax lines")
                        }
                        multiValue={taxLineSelectValue(existingSelectedTaxLine)}
                        options={taxLineSelectOptions(taxLines, t("No tax"))}
                        onChange={value => toggleExistingTaxLines(value)}
                        disabled={regularCombo}
                      />
                      <Input
                        id="existing_price_after_tax"
                        value={existingPriceAfterTax || ""}
                        type="number"
                        prefix="$"
                        label={t("Price after tax")}
                        placeholder="0.00"
                        onChange={e => handleExistingPriceAfterTaxChange(Number(e.target.value))}
                        disabled={regularCombo}
                      />
                    </FormLayout.Group>
                  </FormLayout>
                ) : null}
              </Card.Section>
            </Card>
          ) : null}
          <Card
            title={
              isBootstrapMode
                ? bootstrapState.newOptionValue.trim()
                  ? t("Pricing — new variant ({{value}})", {
                      value: bootstrapState.newOptionValue.trim(),
                    })
                  : t("Pricing — new variant")
                : t("Pricing")
            }
          >
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    id="retail_price"
                    value={variantState.price ?? ""}
                    type="number"
                    prefix="$"
                    label={t("Retail Price")}
                    onChange={e => handlePriceChange(Number(e.target.value))}
                    placeholder="0.00"
                  />
                  <Input
                    id="cost"
                    value={variantState.cost ?? ""}
                    type="number"
                    prefix="$"
                    label={t("secure.facility.product.product_new.015")}
                    onChange={e => handleCostChange(Number(e.target.value))}
                    placeholder="0.00"
                  />
                  <Input
                    id="markup"
                    value={Number(roundNumber(markup, 2)) || ""}
                    onChange={e => handleMarkupChange(Number(e.target.value))}
                    type="number"
                    prefix="%"
                    label={t("Markup")}
                    placeholder="0.00"
                  />
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>
            <Card.Section
              title={t("Price after tax")}
              sectionTitleActions={[
                {
                  content: <FontAwesomeIcon icon={hidePriceAfterTax ? faChevronUp : faChevronDown} />,
                  action: () => setHidePriceAfterTax(!hidePriceAfterTax),
                },
              ]}
            >
              {!hidePriceAfterTax ? (
                <FormLayout>
                  <FormLayout.Group>
                    <SelectNew
                      id="tax-lines-select"
                      label={t("Tax lines")}
                      placeholder={
                        selectedTaxLine.length === 0
                          ? t("No tax")
                          : taxLineNames(selectedTaxLine, taxLines) || t("Select tax lines")
                      }
                      multiValue={taxLineSelectValue(selectedTaxLine)}
                      options={taxLineSelectOptions(taxLines, t("No tax"))}
                      onChange={value => toggleTaxLines(value)}
                      disabled={regularCombo}
                    />
                    <Input
                      id="price_after_tax"
                      value={priceAfterTax || ""}
                      type="number"
                      prefix="$"
                      label={t("Price after tax")}
                      placeholder="0.00"
                      onChange={e => handlePriceAfterTaxChange(Number(e.target.value))}
                      disabled={regularCombo}
                    />
                  </FormLayout.Group>
                </FormLayout>
              ) : null}
            </Card.Section>
          </Card>
        </>
      ) : null}

      {selected === 2 ? (
        <Card
          title={t("secure.facility.product.variant_new.010")}
          subtitle={t("Manage your inventory here.")}
          titleActions={[
            {
              content: (
                <Checkbox
                  id="tracked"
                  size="medium"
                  checked={variantState.tracked}
                  onChange={handleCheckboxChange}
                  label={t("secure.facility.product.variant_new.013")}
                />
              ),
              action: () => {},
            },
          ]}
        >
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  value={variantState.barcode || ""}
                  label={t("secure.facility.product.variant_new.011")}
                  id="barcode"
                  onChange={handleInputChange}
                />
                <Input
                  value={variantState.sku || ""}
                  label={t("secure.facility.product.variant_new.012")}
                  id="sku"
                  onChange={handleInputChange}
                />
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
        </Card>
      ) : null}
    </Page>
  );
}
